import React, { useState, useEffect } from 'react';
import './ManagerBrokerForm.js'; // Make sure you have a corresponding CSS file or remove this line.

// A helper function to format numbers as currency
const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '$0';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(num);
};

const AdminBrokerForm = () => {
    // Helper function to define the initial state, making the reset function cleaner.
    const getInitialState = () => ({
        homeOwnerFirstName: '',
        todaysDate: '',
        phone: '',
        lastName: '',
        email: '',
        date: '',
        housePlan: '',
        id: '',
        lotNum: '',
        brokerName: '',
        brokerPhoneNo: '',
        brokerEmail: '',
        baseHomePrice: '',
        lotPrice: '',
        
        unfinishedBasementDefault: false,
        deckPatioSqFt: '',
        deckPatioX: '',
        deckPatioCheckedDefault: false,
        deckPatioCheckedEditable: false,
        firstPorchDefault: false,
        secondPorchDefault: false,
        patioDoorReplaceNum: '',
        patioDoorReplaceCheckedDefault: false,
        patioDoorReplaceCheckedEditable: false,
        patioDoor6to8Num: '',
        patioDoor6to8CheckedDefault: false,
        patioDoor6to8CheckedEditable: false,
        fourSeasonRoomDefault: false,
        balconySqFt: '',
        balconyX: '',
        balconyCheckedDefault: false,
        balconyCheckedEditable: false,
        studyFullBathDefault: false,
        studyFrenchDoorsDefault: false,
        pantrySinkDefault: false,
        
        wetBarSink: false,
        wetBarIceMaker: false,
        wetBarFridge: false,
        wetBarWineCooler: false,
        mediaRoomSpeakers: false,
        mediaRoomProjectorPrep: false,
        mediaRoomSoundproofing: false,
        gameRoomBuiltIns: false,
        smartHomeHub: false,
        securitySystemPrep: false,
        
        otherValue: '',
        lotOther: '',
        otherNotes: '',
        comments: '',
        totalBaseHome: '',
        totalOptions: '',
        notesAndCommits: '',
        totalFinal: '',
        deposit: '',
        balance: '',
        printName: '',
        signature: '',
        signatureDate: '',
        elevation: '',
    });
    
    // --- DATA & CONSTANTS ---
    const DECK_PATIO_MULTIPLIER = 50;
    const BALCONY_MULTIPLIER = 100;
    const PATIO_DOOR_REPLACE_COST_PER_DOOR = 12000;
    const PATIO_DOOR_6_TO_8_COST_PER_DOOR = 8000;

    const initialDefaultFeatures = [
        { key: 'unfinishedBasementDefault', label: 'Unfinished Basement', cost: 0 },
        { key: 'deckPatioCheckedDefault', label: 'Add 1st floor Deck / Patio', cost: 3500 },
        { key: 'firstPorchDefault', label: 'First Covered porches sf', cost: 2800 },
        { key: 'secondPorchDefault', label: '2nd Covered porches sf', cost: 2200 },
        { key: 'patioDoorReplaceCheckedDefault', label: 'Patio Door Replace for window', cost: 950 },
        { key: 'patioDoor6to8CheckedDefault', label: 'Patio Door 6\' TO 8\'', cost: 1200 },
        { key: 'fourSeasonRoomDefault', label: 'Finished 4 season room.', cost: 8500 },
        { key: 'balconyCheckedDefault', label: 'Balcony', cost: 4500 },
        { key: 'studyFullBathDefault', label: 'Study / 1st FL Full Bath', cost: 5500 },
        { key: 'studyFrenchDoorsDefault', label: 'Study French Doors', cost: 1300 },
        { key: 'pantrySinkDefault', label: 'Pantery Sink And Dishwasher', cost: 1800 },
    ];
    
    const wetBarFeatures = [
        { key: 'wetBarSink', label: 'Wet Bar - Add Sink & Faucet' },
        { key: 'wetBarIceMaker', label: 'Wet Bar - Add Ice Maker' },
        { key: 'wetBarFridge', label: 'Wet Bar - Under-counter Fridge' },
        { key: 'wetBarWineCooler', label: 'Wet Bar - Wine Cooler' },
        { key: 'mediaRoomSpeakers', label: 'Media Room - In-ceiling Speakers' },
        { key: 'mediaRoomProjectorPrep', label: 'Media Room - Projector Pre-wire' },
        { key: 'mediaRoomSoundproofing', label: 'Media Room - Soundproofing Walls' },
        { key: 'gameRoomBuiltIns', label: 'Game Room - Custom Built-ins' },
        { key: 'smartHomeHub', label: 'Add Smart Home Hub' },
        { key: 'securitySystemPrep', label: 'Security System Pre-wire' },
    ];

    const [formData, setFormData] = useState(getInitialState());
    const [dynamicDefaultFeatures, setDynamicDefaultFeatures] = useState(initialDefaultFeatures);
    
    // NEW: State to track the index of the item being dragged
    const [draggedItemIndex, setDraggedItemIndex] = useState(null);

    const [deckPatioCost, setDeckPatioCost] = useState(0);
    const [balconyCost, setBalconyCost] = useState(0);
    const [patioDoorReplaceCost, setPatioDoorReplaceCost] = useState(0);
    const [patioDoor6to8Cost, setPatioDoor6to8Cost] = useState(0);


    // --- CALCULATION EFFECTS ---
    useEffect(() => {
        const length = parseFloat(formData.deckPatioSqFt);
        const width = parseFloat(formData.deckPatioX);
        if (!isNaN(length) && !isNaN(width) && length > 0 && width > 0) {
            setDeckPatioCost(length * width * DECK_PATIO_MULTIPLIER);
        } else { setDeckPatioCost(0); }
    }, [formData.deckPatioSqFt, formData.deckPatioX]);

    useEffect(() => {
        const length = parseFloat(formData.balconySqFt);
        const width = parseFloat(formData.balconyX);
        if (!isNaN(length) && !isNaN(width) && length > 0 && width > 0) {
            setBalconyCost(length * width * BALCONY_MULTIPLIER);
        } else { setBalconyCost(0); }
    }, [formData.balconySqFt, formData.balconyX]);

    useEffect(() => {
        const quantity = parseInt(formData.patioDoorReplaceNum, 10);
        if (!isNaN(quantity) && quantity > 0) {
            setPatioDoorReplaceCost(quantity * PATIO_DOOR_REPLACE_COST_PER_DOOR);
        } else { setPatioDoorReplaceCost(0); }
    }, [formData.patioDoorReplaceNum]);

    useEffect(() => {
        const quantity = parseInt(formData.patioDoor6to8Num, 10);
        if (!isNaN(quantity) && quantity > 0) {
            setPatioDoor6to8Cost(quantity * PATIO_DOOR_6_TO_8_COST_PER_DOOR);
        } else { setPatioDoor6to8Cost(0); }
    }, [formData.patioDoor6to8Num]);

    useEffect(() => {
        const today = new Date();
        const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        setFormData(prevData => ({ ...prevData, todaysDate: formattedDate }));
    }, []);

    const handleChange = (e) => {
        const { id, value, type, checked } = e.target;
        setFormData(prevData => ({ ...prevData, [id]: type === 'checkbox' ? checked : value }));
    };

    const handleClearAll = () => {
        const initialState = getInitialState();
        const today = new Date();
        const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        setFormData({ ...initialState, todaysDate: formattedDate });
        setDynamicDefaultFeatures(initialDefaultFeatures);
        setDeckPatioCost(0);
        setBalconyCost(0);
        setPatioDoorReplaceCost(0);
        setPatioDoor6to8Cost(0);
        console.log("Form cleared!");
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        console.log("Form submitted with data:", formData);
        console.log("Feature Order:", dynamicDefaultFeatures);
    };

    const handleSend = () => {
        console.log("Form data sent (simulated action):", formData);
    };
    
    // --- NEW: Drag and Drop Event Handlers using HTML5 API ---
    const handleDragStart = (e, index) => {
        setDraggedItemIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e) => {
        // Preventing the default behavior is crucial for the onDrop event to fire.
        e.preventDefault(); 
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        if (draggedItemIndex === null || draggedItemIndex === dropIndex) {
            return;
        }

        const items = [...dynamicDefaultFeatures];
        // Remove the dragged item from its original position
        const [reorderedItem] = items.splice(draggedItemIndex, 1);
        // Insert it at the new position
        items.splice(dropIndex, 0, reorderedItem);

        setDynamicDefaultFeatures(items);
    };
    
    const handleDragEnd = () => {
        // Reset the dragged item index after the operation is complete
        setDraggedItemIndex(null);
    };

    return (
        <div className="container my-2 p-3 rounded broker-form-container">
            <div className="broker-form-scroll-wrapper">
                <form onSubmit={handleSubmit}>
                    {/* Header and Top Info */}
                    <div className="d-flex justify-content-between align-items-center mb-1 pb-1 border-bottom">
                        <h2 className="mb-0">Structural Options Selections</h2>
                        <div className="d-flex align-items-center">
                            <div className="bg-light border d-flex justify-content-center align-items-center ms-1" style={{ width: '150px', height: '150px' }}>
                                <img
                                    src="https://images.unsplash.com/photo-1745495707630-acb721c28061?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                                    alt="Home Exterior"
                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="d-flex mb-2 align-items-start">
                        <div className="flex-grow-1 ms-0">
                            <div className="row g-1 mb-1">
                                <div className="col-md-6 d-flex align-items-center">
                                    <label htmlFor="homeOwnerFirstName" className="form-label text-end me-0 mb-0" style={{ width: '100px' }}>Homeowner First Name</label>
                                    <input type="text" id="homeOwnerFirstName" className="form-control form-control-sm" value={formData.homeOwnerFirstName} onChange={handleChange} />
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    <label htmlFor="lastName" className="form-label text-end me-0 mb-0" style={{ width: '100px' }}>Last Name</label>
                                    <input type="text" id="lastName" className="form-control form-control-sm" value={formData.lastName} onChange={handleChange} />
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    <label htmlFor="todaysDate" className="form-label text-end me-0 mb-0" style={{ width: '100px'  }}>Today's Date</label>
                                    <input type="text" id="todaysDate" className="form-control form-control-sm" value={formData.todaysDate} readOnly />
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    <label htmlFor="phone" className="form-label text-end me-0 mb-0" style={{ width: '100px' }}>Phone</label>
                                    <input type="tel" id="phone" className="form-control form-control-sm" value={formData.phone} onChange={handleChange} />
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    <label htmlFor="email" className="form-label text-end me-0 mb-0" style={{ width: '100px' }}>Email</label>
                                    <input type="email" id="email" className="form-control form-control-sm" value={formData.email} onChange={handleChange} />
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    <label htmlFor="date" className="form-label text-end me-0 mb-0" style={{ width: '100px' }}>Date (from user)</label>
                                    <input type="date" id="date" className="form-control form-control-sm" value={formData.date} onChange={handleChange} />
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    <label htmlFor="housePlan" className="form-label text-end me-0 mb-0" style={{ width: '100px' }}>House Plan</label>
                                    <input type="text" id="housePlan" className="form-control form-control-sm" value={formData.housePlan} onChange={handleChange} />
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    <label htmlFor="id" className="form-label text-end me-0 mb-0" style={{ width: '30px' }}>ID:</label>
                                    <input type="text" id="id" className="form-control form-control-sm me-2" style={{ width: '70px' }} value={formData.id} onChange={handleChange} />
                                    <label htmlFor="lotNum" className="form-label text-end me-0 mb-0" style={{ width: '25px' }}>Lot:</label>
                                    <select id="lotNum" className="form-select form-control-sm me-0 ms-1" style={{ width: '90px' }} value={formData.lotNum} onChange={handleChange}>
                                        <option value="">Select Lot</option>
                                        {Array.from({ length: 20 }, (_, i) => i + 1).map(lot => (
                                            <option key={lot} value={lot}>{lot}</option>
                                        ))}
                                    </select>
                                    <label htmlFor="elevation" className="form-label text-end me-0 mb-0" style={{ width: '80px' }}>Elevation</label>
                                    <select id="elevation" className="form-select form-control-sm" style={{ width: '90px' }} value={formData.elevation} onChange={handleChange}>
                                        <option value="">Select Elevation</option>
                                        <option value="a">a</option>
                                        <option value="b">b</option>
                                    </select>
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    <label htmlFor="brokerName" className="form-label text-end me-0 mb-0" style={{ width: '100px' }}>Broker Name</label>
                                    <input type="text" id="brokerName" className="form-control form-control-sm" value={formData.brokerName} onChange={handleChange} />
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    <label htmlFor="brokerPhoneNo" className="form-label text-end me-0 mb-0" style={{ width: '100px' }}>Broker Phone No:</label>
                                    <input type="tel" id="brokerPhoneNo" className="form-control form-control-sm" value={formData.brokerPhoneNo} onChange={handleChange} />
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    <label htmlFor="brokerEmail" className="form-label text-end me-0 mb-0" style={{ width: '100px' }}>Broker Email</label>
                                    <input type="email" id="brokerEmail" className="form-control form-control-sm" value={formData.brokerEmail} onChange={handleChange} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row g-1 mb-1 pt-1 border-top">
                        <div className="col-md-12 d-flex justify-content-end align-items-center">
                            <label htmlFor="baseHomePrice" className="form-label me-2 mb-0">Base Home price</label>
                            <input type="text" id="baseHomePrice" className="form-control form-control-sm text-end me-4" style={{ maxWidth: '100px' }} value={formData.baseHomePrice} onChange={handleChange} />
                            <label htmlFor="lotPrice" className="form-label me-2 mb-0">LOT $</label>
                            <input type="text" id="lotPrice" className="form-control form-control-sm text-end" style={{ maxWidth: '100px' }} value={formData.lotPrice} onChange={handleChange} />
                        </div>
                    </div>
                    <hr />

                    {/* Default Features Section - MODIFIED FOR NATIVE DRAG-AND-DROP */}
                    <div className="container default-features-container">
                        <h3 className="text-center">Default Features</h3>
                        <div>
                            {dynamicDefaultFeatures.map((feature, index) => {
                                // NEW: Apply styling for visual feedback during drag
                                const isDragging = draggedItemIndex === index;
                                const style = {
                                    opacity: isDragging ? 0.5 : 1,
                                    cursor: 'grab',
                                    border: isDragging ? '2px dashed #0d6efd' : '1px solid #dee2e6', // Highlight when dragging
                                    borderRadius: '0.25rem',
                                    marginBottom: '4px',
                                };

                                return (
                                    <div
                                        key={feature.key}
                                        style={style}
                                        draggable="true" // Makes the element draggable
                                        onDragStart={(e) => handleDragStart(e, index)}
                                        onDragOver={handleDragOver}
                                        onDrop={(e) => handleDrop(e, index)}
                                        onDragEnd={handleDragEnd}
                                        className="row g-0 structural-options-row align-items-center py-1"
                                    >
                                        <div className="col-6 ps-1">{feature.label}</div>
                                        <div className="col-4 text-center">
                                            {formData[feature.key] ? formatCurrency(feature.cost) : 'NA'}
                                        </div>
                                        <div className="col-2 d-flex justify-content-center align-items-center">
                                            <div className="form-check m-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={feature.key}
                                                    checked={formData[feature.key]}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                         
                    {/* Editable Features */}
                    <div className="container default-features-container">
                        <h3 className="text-center">Editable Features</h3>
                        
                        <div className="row g-0 structural-options-row align-items-center">
                            <div className="col-6 ps-2 d-flex justify-content-between align-items-center pe-3">
                                <span>Add 1st floor Deck / Patio</span>
                                <strong>{DECK_PATIO_MULTIPLIER}</strong>
                            </div>
                            <div className="col-4 d-flex align-items-center justify-content-end">
                                <input type="number" className="form-control form-control-sm text-center" id="deckPatioSqFt" value={formData.deckPatioSqFt} onChange={handleChange} placeholder="Sq Ft" style={{width: '60px'}} />
                                <span className="px-1">X</span>
                                <input type="number" className="form-control form-control-sm text-center" id="deckPatioX" value={formData.deckPatioX} onChange={handleChange} placeholder="Width" style={{width: '60px'}} />
                                <span className="px-1">=</span>
                                <input type="text" className="form-control form-control-sm text-end" value={deckPatioCost > 0 ? formatCurrency(deckPatioCost) : ''} placeholder="Total" readOnly style={{ width: '85px', flexShrink: 0 }} />
                            </div>
                            <div className="col-2 d-flex justify-content-center align-items-center">
                                <div className="form-check m-0">
                                    <input className="form-check-input" type="checkbox" id="deckPatioCheckedEditable" checked={formData.deckPatioCheckedEditable} onChange={handleChange} />
                                </div>
                            </div>
                        </div>

                        <div className="row g-0 structural-options-row align-items-center">
                            <div className="col-6 ps-2 d-flex justify-content-between align-items-center pe-3">
                                <span>Patio Door Replace for window</span>
                                <strong>{formatCurrency(PATIO_DOOR_REPLACE_COST_PER_DOOR)}</strong>
                            </div>
                            <div className="col-4 d-flex align-items-center justify-content-end">
                                <input type="number" className="form-control form-control-sm ms-2" placeholder="# of doors" id="patioDoorReplaceNum" value={formData.patioDoorReplaceNum} onChange={handleChange} style={{width: '80px'}}/>
                                <span className="px-1">=</span>
                                <input type="text" className="form-control form-control-sm text-end" value={patioDoorReplaceCost > 0 ? formatCurrency(patioDoorReplaceCost) : ''} placeholder="Total" readOnly style={{ width: '85px', flexShrink: 0 }} />
                            </div>
                            <div className="col-2 d-flex justify-content-center align-items-center">
                                <div className="form-check m-0">
                                    <input className="form-check-input" type="checkbox" id="patioDoorReplaceCheckedEditable" checked={formData.patioDoorReplaceCheckedEditable} onChange={handleChange} />
                                </div>
                            </div>
                        </div>

                        <div className="row g-0 structural-options-row align-items-center">
                            <div className="col-6 ps-2 d-flex justify-content-between align-items-center pe-3">
                                <span>Patio Door 6' TO 8'</span>
                                <strong>{formatCurrency(PATIO_DOOR_6_TO_8_COST_PER_DOOR)}</strong>
                            </div>
                            <div className="col-4 d-flex align-items-center justify-content-end">
                                <input type="number" className="form-control form-control-sm ms-2" placeholder="# of doors" id="patioDoor6to8Num" value={formData.patioDoor6to8Num} onChange={handleChange} style={{width: '80px'}} />
                                <span className="px-1">=</span>
                                <input type="text" className="form-control form-control-sm text-end" value={patioDoor6to8Cost > 0 ? formatCurrency(patioDoor6to8Cost) : ''} placeholder="Total" readOnly style={{ width: '85px', flexShrink: 0 }} />
                            </div>
                            <div className="col-2 d-flex justify-content-center align-items-center">
                                <div className="form-check m-0">
                                    <input className="form-check-input" type="checkbox" id="patioDoor6to8CheckedEditable" checked={formData.patioDoor6to8CheckedEditable} onChange={handleChange} />
                                </div>
                            </div>
                        </div>

                        <div className="row g-0 structural-options-row align-items-center">
                            <div className="col-6 ps-2 d-flex justify-content-between align-items-center pe-3">
                                <span>Balcony</span>
                                <strong>{BALCONY_MULTIPLIER}</strong>
                            </div>
                            <div className="col-4 d-flex align-items-center justify-content-end">
                                <input type="number" className="form-control form-control-sm text-center" id="balconySqFt" value={formData.balconySqFt} onChange={handleChange} placeholder="Sq Ft" style={{width: '60px'}} />
                                <span className="px-1">X</span>
                                <input type="number" className="form-control form-control-sm text-center" id="balconyX" value={formData.balconyX} onChange={handleChange} placeholder="Width" style={{width: '60px'}} />
                                <span className="px-1">=</span>
                                <input type="text" className="form-control form-control-sm text-end" value={balconyCost > 0 ? formatCurrency(balconyCost) : ''} placeholder="Total" readOnly style={{ width: '85px', flexShrink: 0 }} />
                            </div>
                            <div className="col-2 d-flex justify-content-center align-items-center">
                                <div className="form-check m-0">
                                    <input className="form-check-input" type="checkbox" id="balconyCheckedEditable" checked={formData.balconyCheckedEditable} onChange={handleChange} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Wet Bar / Media Room Options Section */}
                    <div className="container default-features-container">
                        <h3 className="text-center">Wet Mini Bar / Media Room Options</h3>
                        {wetBarFeatures.map((feature) => (
                            <div key={feature.key} className="row g-0 structural-options-row align-items-center py-1">
                                <div className="col-6 ps-1">{feature.label}</div>
                                <div className="col-4 text-center">NA</div>
                                <div className="col-2 d-flex justify-content-center align-items-center">
                                    <div className="form-check m-0">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={feature.key}
                                            checked={formData[feature.key]}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <hr />

                    {/* Other Section */}
                    <div className="row g-1 mb-1">
                        <div className="col-md-3">
                            <label htmlFor="otherValue" className="form-label text-end w-100">Other</label>
                        </div>
                        <div className="col-md-3">
                            <input type="text" id="otherValue" className="form-control form-control-sm" value={formData.otherValue} onChange={handleChange} />
                        </div>
                        <div className="col-md-2">
                            <label htmlFor="lotOther" className="form-label text-end w-100">LOT</label>
                        </div>
                        <div className="col-md-4">
                            <input type="text" id="lotOther" className="form-control form-control-sm" value={formData.lotOther} onChange={handleChange} />
                        </div>
                        <div className="col-md-3">
                            <label htmlFor="otherNotes" className="form-label text-end w-100">Other</label>
                        </div>
                        <div className="col-md-9">
                            <input type="text" id="otherNotes" className="form-control form-control-sm" value={formData.otherNotes} onChange={handleChange} />
                        </div>
                        <div className="col-md-3">
                            <label htmlFor="comments" className="form-label text-end w-100">Comments</label>
                        </div>
                        <div className="col-md-9">
                            <input type="text" id="comments" className="form-control form-control-sm" value={formData.comments} onChange={handleChange} />
                        </div>
                    </div>

                    {/* Financial Summary */}
                    <div className="row g-1 mb-1 pt-1 border-top">
                        <div className="col-8 text-end fw-bold">Total Base Home</div>
                        <div className="col-1 text-end fw-bold">$</div>
                        <div className="col-3">
                            <input type="text" className="form-control form-control-sm text-end" id="totalBaseHome" value={formData.totalBaseHome} onChange={handleChange} />
                        </div>

                        <div className="col-8 text-end fw-bold">Total options</div>
                        <div className="col-1 text-end fw-bold">$</div>
                        <div className="col-3">
                            <input type="text" className="form-control form-control-sm text-end" id="totalOptions" value={formData.totalOptions} onChange={handleChange} />
                        </div>
                    </div>

                    {/* Notes and Commits / Final Totals */}
                    <div className="row g-1 mb-1 mt-2">
                        <div className="col-md-6 fw-bold border-bottom pb-1">Notes and Commits:</div>
                        <div className="col-md-3"></div>
                        <div className="col-md-3 text-end fw-bold">Initial</div>

                        <div className="col-md-6">
                            <textarea rows="3" className="form-control form-control-sm" id="notesAndCommits" value={formData.notesAndCommits} onChange={handleChange}></textarea>
                        </div>
                        <div className="col-md-6">
                            <div className="row g-0 align-items-center">
                                <div className="col-6 text-end fw-bold">Total</div>
                                <div className="col-1 text-end fw-bold">$</div>
                                <div className="col-5">
                                    <input type="text" className="form-control form-control-sm text-end" id="totalFinal" value={formData.totalFinal} onChange={handleChange} />
                                </div>
                                <div className="col-6 text-end fw-bold">Deposit</div>
                                <div className="col-1 text-end fw-bold">$</div>
                                <div className="col-5">
                                    <input type="text" className="form-control form-control-sm text-end" id="deposit" value={formData.deposit} onChange={handleChange} />
                                </div>
                                <div className="col-6 text-end fw-bold">Balance</div>
                                <div className="col-1 text-end fw-bold">$</div>
                                <div className="col-5">
                                    <input type="text" className="form-control form-control-sm text-end" id="balance" value={formData.balance} onChange={handleChange} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Signature Section */}
                    <div className="row mt-2 pt-1 border-top">
                        <div className="col-md-4 text-center">
                            <div className="signature-line">
                                <input type="text" className="form-control border-0 text-center form-control-sm" id="printName" value={formData.printName} onChange={handleChange} />
                            </div>
                            <div>Print Name</div>
                        </div>
                        <div className="col-md-4 text-center">
                            <div className="signature-line">
                                <input type="text" className="form-control border-0 text-center form-control-sm" id="signature" value={formData.signature} onChange={handleChange} />
                            </div>
                            <div>Signature</div>
                        </div>
                        <div className="col-md-4 text-center">
                            <div className="signature-line">
                                <input type="date" className="form-control border-0 text-center form-control-sm" id="signatureDate" value={formData.signatureDate} onChange={handleChange} />
                            </div>
                            <div>Date</div>
                        </div>
                    </div>

                    {/* Buttons */}
                    <div className="d-flex justify-content-around mt-2">
                        <button type="button" className="btn-custom-clear" onClick={handleClearAll}>
                            CLEAR ALL
                        </button>
                        <button type="submit" className="btn-custom-submit">
                            SAVE
                        </button>
                        <button type="button" className="btn-custom-send" onClick={handleSend}>
                            PRINT
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminBrokerForm;